import { Copy } from "@/components/icons";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  toast,
} from "@/components/kit";
import { copyToClipboard } from "@/modules/chat-tools/presentation/lib/copy-to-clipboard";
import JsBarcodeModule from "jsbarcode";
import { useLayoutEffect, useRef, useState } from "react";

type TrackingBarcodeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trackingNumber: string;
};

const JsBarcode =
  typeof JsBarcodeModule === "function"
    ? JsBarcodeModule
    : (JsBarcodeModule as { default: typeof JsBarcodeModule }).default;

function drawBarcode(canvas: HTMLCanvasElement, value: string) {
  if (typeof JsBarcode !== "function") {
    throw new Error("JsBarcode is not available");
  }
  JsBarcode(canvas, value, {
    format: "CODE128",
    displayValue: true,
    fontSize: 18,
    height: 90,
    width: 2,
    margin: 10,
    background: "#ffffff",
    lineColor: "#111111",
  });
}

export function TrackingBarcodeDialog({
  open,
  onOpenChange,
  trackingNumber,
}: TrackingBarcodeDialogProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawError, setDrawError] = useState(false);
  const value = trackingNumber.trim();

  useLayoutEffect(() => {
    if (!open || !value) return;

    setDrawError(false);
    let cancelled = false;
    let attempts = 0;

    const tryDraw = () => {
      if (cancelled) return;
      const canvas = canvasRef.current;
      if (!canvas) {
        attempts += 1;
        if (attempts < 20) {
          requestAnimationFrame(tryDraw);
        }
        return;
      }
      try {
        drawBarcode(canvas, value);
      } catch {
        if (!cancelled) {
          setDrawError(true);
          toast.error("ບໍ່ສາມາດສ້າງ barcode ໄດ້");
        }
      }
    };

    // Dialog portal ອາດຍັງບໍ່ mount ໃນ frame ດຽວກັນ
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(tryDraw);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [open, value]);

  async function handleCopy() {
    const ok = await copyToClipboard(value);
    if (ok) toast.success("Copy ເລກ tracking ແລ້ວ");
    else toast.error("Copy ບໍ່ສຳເລັດ");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Barcode Tracking</DialogTitle>
          <DialogDescription>
            ສະແກນ ຫຼື ສຳເນົາເລກ tracking ສຳລັບການຈັດສົ່ງ
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4">
          <div className="flex min-h-32 w-full items-center justify-center overflow-x-auto rounded-xl border border-border bg-white p-4">
            {drawError ? (
              <p className="text-center text-destructive text-sm">
                ສ້າງ barcode ບໍ່ສຳເລັດ — ລອງ copy ເລກແທນ
              </p>
            ) : (
              <canvas
                ref={canvasRef}
                className="mx-auto block max-w-full"
              />
            )}
          </div>

          <p className="break-all text-center font-mono text-sm tabular-nums">
            {value}
          </p>

          <Button
            type="button"
            variant="outline"
            className="w-full rounded-full"
            onClick={handleCopy}
          >
            <Copy data-icon="inline-start" />
            Copy ເລກ tracking
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
